import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Search } from 'lucide-react';
import { api } from '../../api';
import { NOME_STATUS, NOME_TIPO, telefone, tiposDe } from '../../componentes/formatos';

export interface DadosCliente { id?: string; tipo: string; razao_social: string; nome_fantasia: string | null; documento: string | null; status: string; observacoes: string | null; versao?: number; documento_completo?: boolean; origem?: string }

interface Receita {
  cnpj: string; razao_social: string; nome_fantasia: string | null; situacao: string; ativa: boolean;
  natureza_juridica: { codigo: string; descricao: string } | null; tipo_sugerido: 'condominio' | 'empresa'; confirmar_tipo: boolean;
  endereco: { logradouro: string | null; numero: string | null; complemento: string | null; bairro: string | null; cidade: string | null; uf: string | null; cep: string | null };
  telefone_receita: string | null; email_receita: string | null; fonte: string; ja_cadastrado: { id: string; nome: string } | null;
}

/** "AVENIDA DAS PALMEIRAS" → "Avenida das Palmeiras" (Receita grava tudo em maiúsculas) */
function capitalizar(t: string | null) {
  if (!t) return null;
  return t.toLowerCase().replace(/(^|\s|\/|-)(\p{L})/gu, (_m, a: string, b: string) => a + b.toUpperCase())
    .replace(/ (Da|De|Do|Das|Dos|E)(?= )/g, (m) => m.toLowerCase())
    .replace(/\b(Ii|Iii|Iv|Vi|Vii|Viii|Ix|Xi|Xii)\b/g, (m) => m.toUpperCase())   // Torre II, Bloco IV
    .replace(/\bSn\b/g, 's/n');
}

export function FormCliente({ inicial, aoSalvar, aoCancelar }: { inicial?: DadosCliente; aoSalvar: (id: string) => void; aoCancelar: () => void }) {
  const [f, setF] = useState<DadosCliente>(inicial ?? { tipo: 'condominio', razao_social: '', nome_fantasia: '', documento: '', status: 'ativo', observacoes: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // edição: documento só é enviado se o usuário mexer nele (quem vê mascarado não "apaga" o documento sem querer)
  const [docAlterado, setDocAlterado] = useState(!inicial);
  const set = (k: keyof DadosCliente) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [receita, setReceita] = useState<Receita | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [criarLocal, setCriarLocal] = useState(true);
  const docLimpo = (f.documento ?? '').replace(/[^0-9A-Za-z]/g, '');
  const doErp = inicial?.origem === 'conta_azul';   // razão social e documento vêm do Conta Azul

  // Consulta na Receita (base pública, pelo servidor): preenche, não salva. Quem confere e salva é o usuário.
  async function consultarCnpj() {
    setErro(null); setConsultando(true);
    try {
      const r = await api<Receita>(`/cnpj/${encodeURIComponent(docLimpo)}`);
      setReceita(r);
      setF((x) => ({
        ...x, documento: r.cnpj, razao_social: r.razao_social,
        nome_fantasia: x.nome_fantasia || capitalizar(r.nome_fantasia) || '',
        ...(inicial ? {} : { tipo: r.tipo_sugerido }),
      }));
    } catch (err) { setReceita(null); setErro(err instanceof Error ? err.message : 'Consulta indisponível.'); }
    finally { setConsultando(false); }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null); setSalvando(true);
    const corpo: Record<string, unknown> = { tipo: f.tipo, razao_social: f.razao_social, nome_fantasia: f.nome_fantasia || null, status: f.status, observacoes: f.observacoes || null };
    if (docAlterado) corpo.documento = f.documento || null;
    try {
      if (inicial?.id) {
        await api(`/clientes/${inicial.id}`, { metodo: 'PATCH', corpo: { ...corpo, versao: inicial.versao } });
        aoSalvar(inicial.id);
      } else {
        const r = await api<{ id: string }>('/clientes', { metodo: 'POST', corpo });
        const e = receita?.endereco;
        if (receita && criarLocal && e && (e.logradouro || e.cep)) {
          // local com o endereço da Receita; coordenadas (check-in) ficam para quem conhece o lugar
          await api(`/clientes/${r.id}/locais`, { metodo: 'POST', corpo: {
            nome: 'Principal', logradouro: capitalizar(e.logradouro), numero: e.numero, complemento: capitalizar(e.complemento),
            bairro: capitalizar(e.bairro), cidade: capitalizar(e.cidade), uf: e.uf, cep: e.cep && /^\d{8}$/.test(e.cep) ? e.cep : null,
          } }).catch(() => {});   // cliente já salvo; local pode ser criado à mão
        }
        aoSalvar(r.id);
      }
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar.');
    } finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <div className="linha-2">
        <label>Tipo
          <select value={f.tipo} onChange={set('tipo')}>{tiposDe(NOME_TIPO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
        <label>Situação
          <select value={f.status} onChange={set('status')} disabled={doErp} title={doErp ? 'Vem do Conta Azul' : undefined}>{tiposDe(NOME_STATUS).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
      </div>
      <label>{f.tipo === 'pessoa' ? 'Nome completo' : 'Razão social'}
        <input required minLength={2} maxLength={200} value={f.razao_social} onChange={set('razao_social')} autoFocus={!doErp} readOnly={doErp} />
        {doErp && <small className="ajuda">Razão social, CPF/CNPJ e situação vêm do Conta Azul. Para corrigir, altere lá — a próxima sincronização traz.</small>}
      </label>
      {f.tipo !== 'pessoa' && (
        <label>Nome fantasia / como é conhecido
          <input maxLength={200} value={f.nome_fantasia ?? ''} onChange={set('nome_fantasia')} placeholder="Ex.: Residencial Paineiras" />
        </label>
      )}
      <label>{f.tipo === 'pessoa' ? 'CPF' : 'CNPJ'}
        {!docAlterado || doErp ? (
          <div className="campo-mascarado">
            <span className="mono">{f.documento ?? 'não informado'}</span>
            {!doErp && <button type="button" className="btn-link" onClick={() => { setDocAlterado(true); setF({ ...f, documento: inicial?.documento_completo ? f.documento : '' }); }}>alterar</button>}
          </div>
        ) : (
          f.tipo === 'pessoa' ? (
            <input value={f.documento ?? ''} onChange={set('documento')} inputMode="text" maxLength={30} placeholder="000.000.000-00" />
          ) : (
            <div className="cnpj-linha">
              <input value={f.documento ?? ''} onChange={(e) => { set('documento')(e); setReceita(null); }} inputMode="text" maxLength={30} placeholder="00.000.000/0000-00" />
              <button type="button" className="btn-fantasma" onClick={consultarCnpj} disabled={consultando || docLimpo.length !== 14}>
                <Search size={15} aria-hidden /> {consultando ? 'Consultando…' : 'Buscar na Receita'}
              </button>
            </div>
          )
        )}
        {f.tipo === 'pessoa' && <small className="ajuda">CPF é dado pessoal (LGPD): fica criptografado e aparece mascarado para quem não é do administrativo.</small>}
      </label>
      {receita && (
        <div className="cnpj-receita" aria-live="polite">
          {receita.ja_cadastrado && receita.ja_cadastrado.id !== inicial?.id && (
            <p className="alerta">Este CNPJ já está cadastrado: <Link to={`/clientes/${receita.ja_cadastrado.id}`}>{receita.ja_cadastrado.nome}</Link>.</p>
          )}
          {!receita.ativa && <p className="alerta">Situação na Receita: {receita.situacao}. Confirme com o cliente antes de fechar contrato.</p>}
          <span><strong>Receita:</strong> {receita.situacao} · {receita.natureza_juridica?.descricao ?? 'natureza não informada'}</span>
          {receita.confirmar_tipo && <span className="nota">Associação de loteamento: marcamos como condomínio — confira o tipo acima.</span>}
          {receita.endereco.logradouro && <span>{capitalizar([receita.endereco.logradouro, receita.endereco.numero].filter(Boolean).join(', '))} — {capitalizar(receita.endereco.bairro)}, {capitalizar(receita.endereco.cidade)}/{receita.endereco.uf}</span>}
          {(receita.telefone_receita || receita.email_receita) && (
            <span className="nota">Contato na Receita: {[receita.telefone_receita && telefone(receita.telefone_receita), receita.email_receita].filter(Boolean).join(' · ')} — costuma ser do contador ou da administradora; não é cadastrado como contato.</span>
          )}
          {!inicial && receita.endereco.logradouro && (
            <label className="check"><input type="checkbox" checked={criarLocal} onChange={(e) => setCriarLocal(e.target.checked)} /> Criar o parque "Principal" com este endereço</label>
          )}
        </div>
      )}
      <label>Observações
        <textarea rows={3} maxLength={4000} value={f.observacoes ?? ''} onChange={set('observacoes')} />
      </label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || f.razao_social.trim().length < 2}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </form>
  );
}
